/**
 * src/pages/CriticalThinking/sections/ClaimAnalyzerSection.tsx
 * Section 3: CLAIM ANALYZER (id="claim-analyzer")
 * Case-based interactive tool with 6 fictional claims working through the 8 framework steps:
 * 1. What exactly is asserted
 * 2. What would make it true or false
 * 3. What evidence is offered
 * 4. How reliable is it
 * 5. Assumptions required
 * 6. Alternative explanations
 * 7. What would weaken it
 * 8. Verdict (well supported, plausible unestablished, not established, insufficient info, contradicted)
 * Also includes Claim Type classification, expert synthesis, and private memory-only note pad.
 */

import React, { useState } from "react";
import { KSection } from "@/components/knowledge/KSection";
import { ToolFrame } from "@/components/knowledge/ToolFrame";
import { Feedback } from "@/components/knowledge/Feedback";
import { Term } from "@/components/knowledge/Term";
import { CLAIM_CASES, type ClaimCase, type ClaimType } from "../data/claims.data";
import { Check, ChevronRight, ChevronLeft, RotateCcw, Sparkles, HelpCircle, FileText, AlertCircle, ArrowRight } from "lucide-react";
export function ClaimAnalyzerSection() {
  const [caseIndex, setCaseIndex] = useState(0);
  const currentCase = CLAIM_CASES[caseIndex];

  // Selected step answers per case
  const [selectedAssertion, setSelectedAssertion] = useState<string | null>(null);
  const [selectedTruthCondition, setSelectedTruthCondition] = useState<string | null>(null);
  const [selectedEvidence, setSelectedEvidence] = useState<string | null>(null);
  const [selectedReliability, setSelectedReliability] = useState<string | null>(null);
  const [selectedAssumption, setSelectedAssumption] = useState<string | null>(null);
  const [selectedAlternative, setSelectedAlternative] = useState<string | null>(null);
  const [selectedWeakener, setSelectedWeakener] = useState<string | null>(null);
  const [selectedVerdict, setSelectedVerdict] = useState<string | null>(null);

  // Claim type selector state
  const [selectedClaimType, setSelectedClaimType] = useState<ClaimType | null>(null);

  // Private memory-only scratchpad
  const [privateNotes, setPrivateNotes] = useState<string>("");

  // Reveal comprehensive feedback
  const [showSynthesis, setShowSynthesis] = useState(false);
  const resetCase = () => {
    setSelectedAssertion(null);
    setSelectedTruthCondition(null);
    setSelectedEvidence(null);
    setSelectedReliability(null);
    setSelectedAssumption(null);
    setSelectedAlternative(null);
    setSelectedWeakener(null);
    setSelectedVerdict(null);
    setSelectedClaimType(null);
    setShowSynthesis(false);
  };
  const handleNextCase = () => {
    resetCase();
    setCaseIndex(prev => (prev + 1) % CLAIM_CASES.length);
  };
  const handlePrevCase = () => {
    resetCase();
    setCaseIndex(prev => (prev - 1 + CLAIM_CASES.length) % CLAIM_CASES.length);
  };
  return <KSection id="claim-analyzer" eyebrow="Interactive Instrument A" title="The Claim Analyzer" intro="Subject real-world assertions to the disciplined 8-step framework. Strip away marketing gloss, viral urgency, and emotional framing to inspect what is actually asserted, what would make it true, and what degree of confidence is genuinely warranted.">
      <ToolFrame title="8-Step Structured Claim Dissector" purpose="Walks systematically through the 8 stages of analytical evaluation: claim classification, precise assertion parsing, truth conditions, evidentiary audits, hidden premises, alternative explanations, falsification conditions, and calibrated verdicts." limits="This is a rule-based educational framework based on curated fictional test specimens, not an AI engine processing unconstrained text. The private scratchpad operates strictly in local memory and is never stored, tracked, or transmitted.">
        {/* Case Selector Navigation Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-5 border-b border-border mb-6">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-primary font-bold uppercase tracking-wider">
              Specimen {caseIndex + 1} of {CLAIM_CASES.length}:
            </span>
            <span className="font-serif text-sm font-bold text-foreground">
              {currentCase.title}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={handlePrevCase} className="p-2 rounded border border-border bg-card/60 hover:bg-card hover:border-primary/50 text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer" title="Previous specimen" aria-label="Previous specimen">
              <ChevronLeft className="size-4" />
            </button>
            <button type="button" onClick={handleNextCase} className="p-2 rounded border border-border bg-card/60 hover:bg-card hover:border-primary/50 text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer" title="Next specimen" aria-label="Next specimen">
              <ChevronRight className="size-4" />
            </button>
            <button type="button" onClick={resetCase} className="px-3 py-2 rounded border border-border bg-card/60 hover:bg-card hover:border-primary/50 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors min-h-[44px] flex items-center gap-1.5 cursor-pointer">
              <RotateCcw className="size-3.5" />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* Case Scenario Presentation Card */}
        <div className="p-4 sm:p-5 rounded-lg border border-border/80 bg-card/50 mb-6 space-y-3">
          <div className="text-xs font-mono text-muted-foreground flex items-center justify-between gap-2 flex-wrap min-w-0">
            <span className="shrink-0">SCENARIO BRIEF</span>
            <span className="text-primary break-words text-right min-w-0">{currentCase.sourceContext}</span>
          </div>

          <p className="font-sans text-xs sm:text-sm text-foreground/90 leading-relaxed">
            {currentCase.scenario}
          </p>

          <blockquote className="p-3.5 rounded border-l-2 border-primary bg-primary/5 font-serif text-sm sm:text-base italic text-foreground leading-snug">
            {currentCase.quote}
          </blockquote>
        </div>

        {/* Claim Type Classification (Factual, Interpretive, Predictive, Normative) */}
        <div className="mb-8 p-4 rounded-lg border border-border/60 bg-card/30">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <span className="font-mono text-xs font-bold text-primary uppercase">
              Pre-Step: Classify Claim Type
            </span>
            <span className="text-xs text-muted-foreground font-sans">
              What kind of statement is this?
            </span>
          </div>

          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {([{
            id: "factual",
            label: "Factual",
            desc: "Descriptive claim about reality"
          }, {
            id: "interpretive",
            label: "Interpretive",
            desc: "Meaning, significance, or framing"
          }, {
            id: "predictive",
            label: "Predictive",
            desc: "Forecasting future conditions"
          }, {
            id: "normative",
            label: "Normative",
            desc: "Values, duties, what ought to be"
          }] as const).map(t => <button key={t.id} type="button" onClick={() => setSelectedClaimType(t.id)} className={`p-2.5 rounded border text-left flex flex-col justify-between transition-all min-h-[44px] cursor-pointer ${selectedClaimType === t.id ? "border-primary bg-primary/15 text-primary shadow-[0_0_10px_var(--glow-primary)]" : "border-border bg-card/40 text-muted-foreground hover:bg-card/70 hover:text-foreground"}`}>
                <span className="font-serif text-xs font-bold">{t.label}</span>
                <span className="text-[10px] leading-tight opacity-80">{t.desc}</span>
              </button>)}
          </div>

          {selectedClaimType && <div className="text-xs p-3 rounded bg-card/60 border border-border/40 font-sans space-y-1">
              <span className="font-mono text-[11px] font-bold text-foreground">
                {selectedClaimType === currentCase.claimType ? "✓ Accurate Classification: " : "Note on Classification: "}
              </span>
              <span className="text-muted-foreground">{currentCase.typeExplanation}</span>
            </div>}
        </div>

        {/* The 8-Step Interactive Framework */}
        <div className="space-y-6">
          {/* Step 1: Exact Assertion */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">1</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                What exactly is asserted?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Select the most precise reading of what the statement claims, avoiding vague generalities or exaggerated caricatures:
            </p>
            <div className="space-y-2">
              {currentCase.steps.assertionOptions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedAssertion(opt.id)} className={`crit-option-btn ${selectedAssertion === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedAssertion === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.label}</p>
                    {selectedAssertion === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 2: Truth Conditions */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">2</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                What would make it true or false?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              What empirical facts, observations, or logical conditions would decisively substantiate or refute this claim?
            </p>
            <div className="space-y-2">
              {currentCase.steps.truthConditions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedTruthCondition(opt.id)} className={`crit-option-btn ${selectedTruthCondition === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedTruthCondition === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.label}</p>
                    {selectedTruthCondition === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 3: Evidence Offered */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">3</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                What evidence is offered right now?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Isolate what the source actually presents to back up their assertion, separate from marketing tone:
            </p>
            <div className="space-y-2">
              {currentCase.steps.evidenceOffered.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedEvidence(opt.id)} className={`crit-option-btn ${selectedEvidence === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedEvidence === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.label}</p>
                    {selectedEvidence === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 4: Reliability of Evidence */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">4</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                How reliable is that evidence?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Assess conflicts of interest, sample size, methodology, and potential for confirmation bias:
            </p>
            <div className="space-y-2">
              {currentCase.steps.reliabilityOptions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedReliability(opt.id)} className={`crit-option-btn ${selectedReliability === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedReliability === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.label}</p>
                    {selectedReliability === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 5: Assumptions Required */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">5</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                What hidden assumptions are required?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Identify the unstated bridges connecting the offered evidence to the bold conclusion:
            </p>
            <div className="space-y-2">
              {currentCase.steps.assumptionsOptions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedAssumption(opt.id)} className={`crit-option-btn ${selectedAssumption === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedAssumption === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.label}</p>
                    {selectedAssumption === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 6: Alternative Explanations */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">6</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                What alternative explanations exist?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              What confounding factors could easily explain the same observed outcome?
            </p>
            <div className="space-y-2">
              {currentCase.steps.alternativesOptions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedAlternative(opt.id)} className={`crit-option-btn ${selectedAlternative === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedAlternative === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.label}</p>
                    {selectedAlternative === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 7: What would weaken it? */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">7</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                What new evidence would decisively weaken this claim?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Define the falsifying observation: what empirical finding would force you to lower your credence?
            </p>
            <div className="space-y-2">
              {currentCase.steps.weakenersOptions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedWeakener(opt.id)} className={`crit-option-btn ${selectedWeakener === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedWeakener === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.label}</p>
                    {selectedWeakener === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 8: Calibrated Verdict */}
          <div className="p-4 rounded-lg border border-primary/40 bg-card/30 space-y-3">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">8</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                How strong a conclusion is justified?
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Choose the calibrated epistemic stance justified by the available evidence:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {currentCase.steps.verdictOptions.map(v => <button key={v.id} type="button" onClick={() => setSelectedVerdict(v.id)} className={`p-3 rounded-lg border text-left flex flex-col justify-between transition-all min-h-[44px] cursor-pointer ${selectedVerdict === v.id ? "border-primary bg-primary/15 text-foreground shadow-[0_0_12px_var(--glow-primary)]" : "border-border bg-card/40 text-muted-foreground hover:bg-card hover:text-foreground"}`}>
                  <span className="font-serif text-xs font-bold text-foreground mb-1">
                    {v.label}
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed font-sans">
                    {v.rationale}
                  </p>
                </button>)}
            </div>
          </div>

          {/* Private Notes Scratchpad (Clearly labelled as private memory-only) */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/20 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap min-w-0">
              <span className="font-mono text-xs text-primary flex items-center gap-1.5 font-bold">
                <FileText className="size-3.5 shrink-0" />
                <span className="break-words">Private Analytical Scratchpad (Memory Only)</span>
              </span>
              <span className="text-[10px] font-mono text-muted-foreground uppercase break-words">
                Not stored • Not analyzed • Private to your browser session
              </span>
            </div>
            <textarea value={privateNotes} onChange={e => setPrivateNotes(e.target.value)} placeholder="Record your own independent observations or reflections here. This text stays strictly in local component memory and is never uploaded anywhere..." rows={2} className="w-full text-base sm:text-sm p-3 rounded border border-border bg-card/60 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          </div>

          {/* Reveal Synthesis Button */}
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
            <button type="button" onClick={() => setShowSynthesis(prev => !prev)} className="ql-neon-btn w-full sm:w-auto px-6 py-3 text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer">
              <Sparkles className="size-4" />
              <span>{showSynthesis ? "Hide Expert Reasoning" : "Reconstruct Full Expert Reasoning"}</span>
            </button>

            <button type="button" onClick={handleNextCase} className="ql-glass-btn w-full sm:w-auto px-5 py-2.5 text-xs font-mono text-foreground hover:text-primary flex items-center justify-center gap-2 cursor-pointer">
              <span>Next Specimen ({caseIndex + 2 > CLAIM_CASES.length ? 1 : caseIndex + 2})</span>
              <ArrowRight className="size-3.5" />
            </button>
          </div>

          {/* Expert Synthesis Panel */}
          {showSynthesis && <div className="space-y-4 pt-4 border-t border-border/80">
              <Feedback tone={selectedVerdict === currentCase.steps.correctVerdictId ? "good" : "partial"} title={`Expert Synthesis: ${currentCase.title}`}>
                <p className="font-sans text-xs sm:text-sm leading-relaxed mb-3">
                  {currentCase.expertSynthesis.summary}
                </p>

                <div className="p-3 rounded bg-card/60 border border-border/50 text-xs text-foreground/90 space-y-1.5 mb-2">
                  <strong className="text-primary font-mono block">
                    Epistemic Stance: Why '{currentCase.steps.correctVerdictId.replace("-", " ").toUpperCase()}'
                  </strong>
                  <p className="text-muted-foreground">
                    {currentCase.expertSynthesis.whyInsufficientInfoOrNotEstablished}
                  </p>
                </div>

                <div className="p-3 rounded bg-card/60 border border-border/50 text-xs text-foreground/90 space-y-1.5">
                  <strong className="text-foreground font-mono block">
                    Confounding Dynamics & Plausible Alternatives:
                  </strong>
                  <p className="text-muted-foreground">
                    {currentCase.expertSynthesis.plausibleAlternativesDiscussion}
                  </p>
                </div>
              </Feedback>
            </div>}
        </div>
      </ToolFrame>
    </KSection>;
}